import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { Badge, Banner, Button, Card, EmptyState, Mono, PageBody, PageHeader, Select, StackedList } from "../ui/Field";

type Block = { id: string; componentKey: string; props: Record<string, unknown> };
type SDoc = { id: string; key: string; name: string; draft_revision: number; live_revision: number | null; revision: { blocks: Block[] } | null };

export default function SnippetEditor() {
  const { slug, id } = useParams();
  const [doc, setDoc] = useState<SDoc | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const saveTimer = useRef<number | null>(null);

  const load = useCallback(() => api.get<SDoc>(`/api/sites/${slug}/snippets/${id}`).then((r) => { setDoc(r); setDirty(false); }).catch((e) => setErr((e as Error).message)), [slug, id]);
  useEffect(() => { load(); }, [load]);

  async function save() {
    if (!doc?.revision) return;
    setBusy(true);
    try {
      await api.put(`/api/sites/${slug}/snippets/${id}/draft`, { blocks: doc.revision.blocks });
      setDirty(false);
      load();
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  }
  async function publish() {
    if (dirty) await save();
    await api.post(`/api/sites/${slug}/snippets/${id}/publish`);
    load();
  }

  useEffect(() => {
    if (!dirty) return;
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(save, 1500);
    return () => { if (saveTimer.current) window.clearTimeout(saveTimer.current); };
    // eslint-disable-next-line
  }, [dirty]);

  function add(key: string) {
    const blk: Block = { id: `blk-${Math.random().toString(36).slice(2, 10)}`, componentKey: key, props: {} };
    setDoc((d) => d?.revision ? { ...d, revision: { ...d.revision, blocks: [...d.revision.blocks, blk] } } : d);
    setDirty(true);
  }

  if (err && !doc) return <PageBody><Banner kind="error">{err}</Banner></PageBody>;
  if (!doc) return <PageBody><div className="h-32 bg-[var(--color-paper-2)] animate-pulse rounded" /></PageBody>;
  const blocks = doc.revision?.blocks ?? [];

  return (
    <PageBody>
      <Link to={`/sites/${slug}/snippets`} className="text-[var(--color-muted)] hover:text-[var(--color-ink)] text-[12px] no-underline">← Snippets</Link>
      <PageHeader
        eyebrow={`№ Snippet · ${doc.key}`}
        title={doc.name}
        description={
          <span className="flex items-center gap-2 flex-wrap">
            <Mono className="text-[11px]">draft {doc.draft_revision}{doc.live_revision ? ` · live ${doc.live_revision}` : ""}</Mono>
            {dirty ? <span className="text-[var(--color-amber)] text-[12px]">· Unsaved</span> : <span className="text-[var(--color-leaf)] text-[12px]">· Saved</span>}
          </span>
        }
        actions={
          <>
            <Button variant="ghost" disabled={!dirty || busy} onClick={save}>{busy ? "Saving…" : "Save"}</Button>
            <Button onClick={publish}>Publish</Button>
          </>
        }
      />
      {err && <Banner kind="error">{err}</Banner>}

      <Card number="B1" title="Blocks" description="Stack components inside this snippet. They render in order wherever the snippet is dropped.">
        <Select
          label="Add a block"
          onChange={(e) => { const v = e.target.value; if (v) { add(v); e.currentTarget.value = ""; } }}
          defaultValue=""
        >
          <option value="" disabled>Choose a component…</option>
          <option value="hero.centered">Hero · centered</option>
          <option value="content.section">Content · section</option>
          <option value="cards.grid">Cards · grid</option>
          <option value="layout.spacer">Layout · spacer</option>
        </Select>

        {blocks.length === 0 ? (
          <EmptyState title="Empty snippet." description="Pick a component above to begin composing." />
        ) : (
          <StackedList>
            {blocks.map((b, i) => (
              <li key={b.id} className="flex items-center justify-between gap-4 py-3">
                <div className="flex items-baseline gap-3">
                  <Mono className="text-[11px] text-[var(--color-muted)]">{String(i + 1).padStart(2, "0")}</Mono>
                  <span className="text-[13.5px] text-[var(--color-ink)]"><Mono>{b.componentKey}</Mono></span>
                  <Badge>block</Badge>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setDoc((d) => d?.revision ? { ...d, revision: { ...d.revision, blocks: d.revision.blocks.filter((x) => x.id !== b.id) } } : d);
                    setDirty(true);
                  }}
                >
                  Remove
                </Button>
              </li>
            ))}
          </StackedList>
        )}
      </Card>
    </PageBody>
  );
}
